import clsx from "clsx";
import React, { FC } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import SwipeableViews from "react-swipeable-views";

import {
  characterSelectors,
  CharacterStatusSlug,
  CharacterTheme,
  FormatUtils,
  rulesEngineSelectors,
} from "@dndbeyond/character-rules-engine/es";
import CharacterSheetSvg from "@dndbeyond/fontawesome-cache/svgs/light/address-card.svg";
import GearSvg from "@dndbeyond/fontawesome-cache/svgs/solid/gear.svg";
import { GameLogNotificationWrapper } from "@dndbeyond/game-log-components";

import { Link } from "~/components/Link";
import { PremadeCharacterEditStatus } from "~/components/PremadeCharacterEditStatus";
import { Tooltip } from "~/components/Tooltip";
import { RouteKey, SectionKey } from "~/subApps/builder/constants";

import { appEnvSelectors } from "../../../Shared/selectors";
import { MobileMessengerUtils } from "../../../Shared/utils";
import { navigationConfig } from "../../config";
import { RouteDef, SettingsLocationState } from "../../config/navigation";
import { builderEnvSelectors, builderSelectors } from "../../selectors";
import { BuilderAppState } from "../../typings";
import HelpTextManager from "../HelpTextManager";
import styles from "./styles.module.css";

const SETTINGS_TOOLTIP = "Character Settings";

interface Props {
  characterId: number | null;
  builderMethod: string | null;
  sections: Array<any>;
  firstAvailableSectionRoutes: any;
  isCharacterSheetReady: boolean;
  characterSheetUrl: string;
  activeSectionIdx: number;
  isMobile: boolean;
  characterStatus: CharacterStatusSlug | null;
  isReadonly: boolean;
  theme: CharacterTheme;
  isBuilderFlowExperimentEnabled: boolean;
  isSettingsActive: boolean;
}
const NavigationSections: FC<Props> = ({
  characterId,
  isCharacterSheetReady,
  characterSheetUrl,
  theme,
  sections,
  firstAvailableSectionRoutes,
  activeSectionIdx,
  builderMethod,
  isMobile,
  characterStatus,
  isReadonly,
  isBuilderFlowExperimentEnabled,
  isSettingsActive,
}) => {
  const location = useLocation();

  const handleSheetShowClick = () => {
    if (characterId !== null) {
      MobileMessengerUtils.sendMessage(
        MobileMessengerUtils.createShowCharacterSheetMessage(characterId)
      );
    }
  };
  const renderCharacterSheetLink = () => {
    //trading out a Router Link for a div if not ready for accessibility
    const Component = isCharacterSheetReady ? Link : "div";
    const CharacterSheetLink = () => (
      <>
        <Component
          className={clsx([
            styles.characterSheetIcon,
            !isCharacterSheetReady && styles.disabled,
          ])}
          href={isCharacterSheetReady ? characterSheetUrl : undefined}
          onClick={handleSheetShowClick}
          userouter={isCharacterSheetReady ? true : undefined}
          data-tooltip-place="bottom"
          data-tooltip-id={"character-sheet-tooltip"}
          data-tooltip-content={
            isCharacterSheetReady
              ? "Go to Character Sheet"
              : "Character is incomplete"
          }
        >
          <CharacterSheetSvg
            className={clsx([
              styles.characterSheetIcon,
              !isCharacterSheetReady && styles.disabled,
            ])}
          />
        </Component>
        <Tooltip id="character-sheet-tooltip" />
      </>
    );

    return (
      <div
        className={clsx([
          "builder-sections-sheet",
          !isCharacterSheetReady && "builder-sections-sheet-disabled",
        ])}
      >
        <GameLogNotificationWrapper
          themeColor={theme.themeColor}
          gameLogIsOpen={false}
          notificationOnClick={() => {}}
          isCharacterBuilder
        >
          <CharacterSheetLink />
        </GameLogNotificationWrapper>
      </div>
    );
  };

  // Remember where the user came from so the settings page can continue back there,
  // keeping the original page if they're already on settings
  const getSettingsLocationState = (): SettingsLocationState =>
    isSettingsActive ? location.state : { returnTo: location.pathname };

  const getSectionLinkState = (section): SettingsLocationState | undefined => {
    if (!isBuilderFlowExperimentEnabled || section.key !== SectionKey.HOME) {
      return undefined;
    }

    return getSettingsLocationState();
  };

  // Mobile only, where it replaces the Settings tab
  const renderSettingsLink = () => {
    if (!isBuilderFlowExperimentEnabled) {
      return null;
    }

    return (
      <div className="builder-sections-settings">
        <Link
          href={navigationConfig
            .getRouteDefPath(RouteKey.HOME_BASIC_INFO)
            .replace(":characterId", String(characterId))}
          state={getSettingsLocationState()}
          className={clsx([
            "builder-sections-link",
            "builder-sections-settings-link",
            isSettingsActive && "builder-sections-link-active",
          ])}
          userouter
          aria-label={SETTINGS_TOOLTIP}
          data-tooltip-place="bottom"
          data-tooltip-id="builder-settings-tooltip"
          data-tooltip-content={SETTINGS_TOOLTIP}
        >
          <GearSvg className="builder-sections-settings-icon" />
        </Link>
        <Tooltip id="builder-settings-tooltip" />
      </div>
    );
  };

  const renderNonMobileUi = () => (
    <div className="builder-sections builder-sections-large">
      {!isBuilderFlowExperimentEnabled && <HelpTextManager />}
      {renderCharacterSheetLink()}
      <div className="builder-sections-large-routes">
        {sections.map((section) => {
          const firstRoute = firstAvailableSectionRoutes[section.key];
          let clsNames: Array<string> = [
            "builder-sections-link",
            `builder-sections-${FormatUtils.slugify(section.key)}`,
          ];
          if (section.active) {
            clsNames.push("builder-sections-link-active");
          }
          return (
            <div className="builder-sections-view" key={section.key}>
              <Link
                href={firstRoute.path.replace(":characterId", characterId)}
                className={clsNames.join(" ")}
                state={getSectionLinkState(section)}
                userouter
              >
                {section.getLabel()}
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderMobileUi = () => {
    const scrollbarStyles: React.CSSProperties = {
      padding: "0 35%",
    };

    return (
      <div className="builder-sections builder-sections-small">
        {!isBuilderFlowExperimentEnabled && <HelpTextManager />}
        {renderSettingsLink()}
        {renderCharacterSheetLink()}
        <SwipeableViews
          index={activeSectionIdx}
          style={scrollbarStyles}
          className="builder-sections-views"
          ignoreNativeScroll={true}
        >
          {sections.map((section) => {
            const firstRoute = firstAvailableSectionRoutes[section.key];
            let clsNames: Array<string> = [
              "builder-sections-link",
              `builder-sections-${FormatUtils.slugify(section.key)}`,
            ];
            if (section.active) {
              clsNames.push("builder-sections-link-active");
            }
            return (
              <div className="builder-sections-view" key={section.key}>
                <Link
                  href={firstRoute.path.replace(":characterId", characterId)}
                  className={clsNames.join(" ")}
                  state={getSectionLinkState(section)}
                  userouter
                >
                  {section.getLabel()}
                </Link>
              </div>
            );
          })}
        </SwipeableViews>
      </div>
    );
  };

  if (builderMethod === null) {
    return null;
  }

  return isMobile ? (
    <>
      {renderMobileUi()}
      <PremadeCharacterEditStatus
        characterStatus={characterStatus}
        isReadonly={isReadonly}
        isBuilderView
      />
    </>
  ) : (
    <>
      {renderNonMobileUi()}
      <PremadeCharacterEditStatus
        characterStatus={characterStatus}
        isReadonly={isReadonly}
        isBuilderView
      />
    </>
  );
};

function mapStateToProps(state: BuilderAppState, ownProps) {
  const currentPath = ownProps.pathname;
  const currentRouteDef = navigationConfig.getCurrentRouteDef(currentPath);
  const isBuilderFlowExperimentEnabled =
    builderSelectors.getIsBuilderFlowExperimentEnabled(state);
  const isMobile = appEnvSelectors.getIsMobile(state);
  let sections = navigationConfig.getNavigationSections(
    builderSelectors.getBuilderMethod(state),
    currentRouteDef,
    isBuilderFlowExperimentEnabled
  );

  // On mobile, settings is reached from the cog instead of a section tab
  if (isBuilderFlowExperimentEnabled && isMobile) {
    sections = sections.filter((section) => section.key !== SectionKey.HOME);
  }

  let activeSectionIdx: number = 0;
  sections.forEach((section, idx) => {
    if (section.active) {
      activeSectionIdx = idx;
    }
  });

  return {
    sections,
    firstAvailableSectionRoutes:
      builderSelectors.getFirstAvailableSectionRoutes(state),
    isCharacterSheetReady: builderSelectors.checkIsCharacterSheetReady(state),
    characterSheetUrl: builderEnvSelectors.getCharacterSheetUrl(state),
    builderMethod: builderSelectors.getBuilderMethod(state),
    activeSectionIdx,
    isMobile,
    characterStatus: characterSelectors.getStatusSlug(state),
    isReadonly: appEnvSelectors.getIsReadonly(state),
    theme: rulesEngineSelectors.getCharacterTheme(state),
    isBuilderFlowExperimentEnabled,
    isSettingsActive:
      (currentRouteDef as RouteDef | null)?.key === RouteKey.HOME_BASIC_INFO,
  };
}

export default connect(mapStateToProps)(NavigationSections);
